/**
 * Performance metrics storage
 */
interface Metrics {
	fps: number;
	minFps: number;
	maxFps: number;
	avgFps: number;
	frameTime: number; // ms
	updateTime: number; // ms
	renderTime: number; // ms
	entityCounts: Record<string, number>;
	memory?: {
		totalJSHeapSize: number;
		usedJSHeapSize: number;
		jsHeapSizeLimit: number;
	};
}

/**
 * Performance Monitor HUD
 * Displays real-time performance stats
 */
export class PerformanceMonitor {
	private static instance: PerformanceMonitor;

	private container!: HTMLDivElement;
	private statsElement!: HTMLPreElement;
	private canvas!: HTMLCanvasElement;
	private ctx!: CanvasRenderingContext2D;

	private isVisible: boolean = false;
	private metrics: Metrics = {
		fps: 60,
		minFps: 60,
		maxFps: 60,
		avgFps: 60,
		frameTime: 0,
		updateTime: 0,
		renderTime: 0,
		entityCounts: {},
	};

	// Frame history for graphing
	private fpsHistory: number[] = new Array(100).fill(60);

	// Measurement timestamps
	private marks: Map<string, number> = new Map();

	private constructor() {
		this.createDOM();
		this.startLoop();
	}

	public static getInstance(): PerformanceMonitor {
		if (!PerformanceMonitor.instance) {
			PerformanceMonitor.instance = new PerformanceMonitor();
		}
		return PerformanceMonitor.instance;
	}

	private createDOM(): void {
		this.container = document.createElement('div');
		Object.assign(this.container.style, {
			position: 'fixed',
			top: '10px',
			left: '10px',
			backgroundColor: 'rgba(0, 0, 0, 0.8)',
			color: '#0f0',
			padding: '10px',
			borderRadius: '4px',
			zIndex: '9999',
			fontFamily: 'monospace',
			fontSize: '12px',
			pointerEvents: 'none', // Click-through
			display: 'none',
			width: '240px',
			boxShadow: '0 0 10px rgba(0,0,0,0.5)',
			border: '1px solid #333'
		});

		this.statsElement = document.createElement('pre');
		Object.assign(this.statsElement.style, {
			margin: '0',
			marginBottom: '5px',
			whiteSpace: 'pre-wrap'
		});

		this.canvas = document.createElement('canvas');
		this.canvas.width = 220;
		this.canvas.height = 50;
		Object.assign(this.canvas.style, {
			backgroundColor: '#111',
			border: '1px solid #333'
		});
		this.ctx = this.canvas.getContext('2d')!;

		this.container.appendChild(this.statsElement);
		this.container.appendChild(this.canvas);
		document.body.appendChild(this.container);
	}

	private startLoop(): void {
		// Reset stats every second
		setInterval(() => {
			this.metrics.minFps = this.metrics.fps;
			this.metrics.maxFps = this.metrics.fps;
		}, 1000);
	}

	/**
	 * Toggle HUD visibility
	 */
	public toggle(): void {
		this.isVisible = !this.isVisible;
		this.container.style.display = this.isVisible ? 'block' : 'none';
	}

	/**
	 * Start measuring a labeled duration (e.g. 'update', 'render')
	 */
	public startMeasure(label: string): void {
		if (!this.isVisible) return;
		this.marks.set(label, performance.now());
	}

	/**
	 * End measuring a labeled duration
	 */
	public endMeasure(label: string): void {
		if (!this.isVisible) return;
		const start = this.marks.get(label);
		if (start) {
			const duration = performance.now() - start;
			if (label === 'update') this.metrics.updateTime = duration;
			if (label === 'render') this.metrics.renderTime = duration;
		}
	}

	/**
	 * Update a generic counter/value
	 */
	public setMetric(label: string, value: number): void {
		this.metrics.entityCounts[label] = value;
	}

	/**
	 * Call once per frame
	 * @param dt Delta time in seconds
	 */
	public update(dt: number): void {
		if (!this.isVisible) return;

		const fps = 1 / dt; // Instantaneous FPS

		// Smoothing
		this.metrics.fps = this.metrics.fps * 0.9 + fps * 0.1;
		this.metrics.frameTime = dt * 1000;

		this.metrics.minFps = Math.min(this.metrics.minFps, fps);
		this.metrics.maxFps = Math.max(this.metrics.maxFps, fps);

		// Update history
		this.fpsHistory.shift();
		this.fpsHistory.push(this.metrics.fps);

		this.updateDisplay();
		this.updateGraph();
	}

	private updateDisplay(): void {
		// Memory
		let memoryInfo = '';
		const mem = (performance as any).memory;
		if (mem) {
			const used = (mem.usedJSHeapSize / 1048576).toFixed(1);
			const total = (mem.totalJSHeapSize / 1048576).toFixed(1);
			memoryInfo = `\nMem: ${used} / ${total} MB`;
		}

		// Entity counts
		let entities = '';
		for (const [key, val] of Object.entries(this.metrics.entityCounts)) {
			entities += `\n${key}: ${val}`;
		}

		this.statsElement.textContent =
			`FPS: ${this.metrics.fps.toFixed(1)} (Min: ${this.metrics.minFps.toFixed(0)}, Max: ${this.metrics.maxFps.toFixed(0)})` +
			`\nFrame: ${this.metrics.frameTime.toFixed(2)}ms` +
			`\nUpdate: ${this.metrics.updateTime.toFixed(2)}ms` +
			`\nRender: ${this.metrics.renderTime.toFixed(2)}ms` +
			memoryInfo +
			entities;
	}

	private updateGraph(): void {
		const w = this.canvas.width;
		const h = this.canvas.height;

		this.ctx.clearRect(0, 0, w, h);

		// Draw 60 FPS line (green)
		const y60 = h - (60 / 120) * h;
		this.ctx.strokeStyle = '#004400';
		this.ctx.beginPath();
		this.ctx.moveTo(0, y60);
		this.ctx.lineTo(w, y60);
		this.ctx.stroke();

		// Draw 30 FPS line (red)
		const y30 = h - (30 / 120) * h;
		this.ctx.strokeStyle = '#440000';
		this.ctx.beginPath();
		this.ctx.moveTo(0, y30);
		this.ctx.lineTo(w, y30);
		this.ctx.stroke();

		// Draw Graph
		this.ctx.strokeStyle = '#0f0';
		this.ctx.beginPath();
		for (let i = 0; i < this.fpsHistory.length; i++) {
			const val = this.fpsHistory[i] ?? 0;
			const x = (i / this.fpsHistory.length) * w;
			// Map 0-120 FPS to h-0 height
			const y = h - Math.min(val / 120, 1) * h;
			if (i === 0) this.ctx.moveTo(x, y);
			else this.ctx.lineTo(x, y);
		}
		this.ctx.stroke();
	}
}
